/**
 * ganttLayout.js — pure geometry/layout math shared by every Gantt-shaped view in the app.
 *
 * Extracted from GanttModal.jsx (2026-09-08) so the WBS Template editor's timeline visualizer
 * (WbsTemplateEditor.jsx) and the real per-project Gantt draw from the SAME window/day-cell/phase-
 * grouping/dependency-geometry code — a fix to how weekends shade, how phase bands compute, or how
 * critical path is detected lands on both automatically. No React here, no I/O — every export is a
 * pure function of the rows/deps/zoom it's given.
 *
 * `rows` anywhere below means the normalized shape both callers already produce:
 *   { id, name, earliest_start, latest_finish, duration_days, date_pinned?, milestone_behavior? }
 * `deps` means [{ milestone_id, predecessor_id }].
 */

import { parseDate, formatDate, addDays, dayDiff } from './cascade.js'

export function todayUTC() {
  const n = new Date()
  return new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate(), 12))
}

/** Short display date, e.g. "Aug 12". */
export function fmtShort(s) {
  const d = parseDate(s)
  return d ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—'
}

// "Locked" for CASCADE/display purposes: Fixed/Scheduled milestones (e.g. a Public Hearing date set
// by an outside agency) read as gray/🔒 and never count as critical-path. Simulated WBS rows carry
// neither field, so this is always false for them — harmless.
export function isLocked(row) {
  // Since 2026-09-24 only a real, locked date reads as locked: a Fixed/Scheduled type (Public Hearing)
  // with no date set yet flows with the schedule like anything else.
  return !!row.date_pinned
}

// Raw float (slack) in days: (LFT − EST) − Duration. ≤ 0 means no slack → critical.
export function rawSlackOf(row) {
  const e = parseDate(row.earliest_start), l = parseDate(row.latest_finish)
  if (!e || !l) return null
  return dayDiff(e, l) - (row.duration_days || 0)
}

/* ── Touch time and the real critical path (Matt, 2026-09-24) ─────────────────────────────────────
   A milestone row carries two different numbers. Its DATES (earliest start → latest finish) are the
   window the schedule gives it; the stored `duration_days` is that window's length (the overnight
   scheduler keeps it so, by the original Airtable design — "Duration" was always the gap between the
   dates, and "Days Budgeted" was meant to be the work). The WORK is the touch time: the curated
   estimate for the milestone's type (`touchDays` on the row, from deliverable_type_effort_days). The
   Gantt draws the dark fill from touch time, never from duration_days. */

const isWeekday = (d) => { const w = d.getUTCDay(); return w !== 0 && w !== 6 }

/** Working days a window offers, start and finish days included (a one-day bar offers 1). */
export function workdaysInWindow(est, lft) {
  const s = parseDate(est), e = parseDate(lft)
  if (!s || !e || e < s) return 0
  let n = 0
  for (let d = s; d <= e; d = addDays(d, 1)) if (isWeekday(d)) n++
  return n
}

/** Calendar days from `est` needed to fit `work` working days (fractional work allowed). */
export function calendarSpanForWork(est, work) {
  const s = parseDate(est)
  if (!s || !(work > 0)) return 0
  let left = work, days = 0, d = s
  while (left > 0 && days < 3660) {
    if (isWeekday(d)) { const take = Math.min(1, left); left -= take; days += take } else days += 1
    d = addDays(d, 1)
  }
  return days
}

/** A bar squeezed into fewer working days than its touch time. */
export function isCompressed(row) {
  if (row.touchDays == null || !(row.touchDays > 0)) return false
  return workdaysInWindow(row.earliest_start, row.latest_finish) < row.touchDays
}

/**
 * The real critical path: the open milestones whose slip would move the project's finish. A row's
 * float is the working days it can slip before it pushes the last open milestone: for a row with open
 * successors, the smallest (gap to that successor + that successor's float); for a row with none, the
 * working days between its finish and the project's finish. The gap is the working days strictly
 * between its finish and the successor's start — zero when the successor starts the next workday,
 * which is exactly how the cascade places successors. Posted rows are done and never critical.
 */
export function computeNetworkCriticalIds(rows, deps) {
  const open = rows.filter(r => !r.posted && parseDate(r.earliest_start) && parseDate(r.latest_finish))
  const byId = new Map(open.map(r => [r.id, r]))
  if (!open.length) return new Set()
  const end = open.map(r => parseDate(r.latest_finish)).reduce((a, b) => (b > a ? b : a))
  const succOf = new Map()
  for (const d of deps || []) {
    if (!byId.has(d.predecessor_id) || !byId.has(d.milestone_id)) continue
    if (!succOf.has(d.predecessor_id)) succOf.set(d.predecessor_id, [])
    succOf.get(d.predecessor_id).push(d.milestone_id)
  }
  const between = (a, b) => {           // working days strictly between dates a and b
    let n = 0
    for (let d = addDays(a, 1); d < b; d = addDays(d, 1)) if (isWeekday(d)) n++
    return b > a ? n : -workdaysInWindow(formatDate(b), formatDate(a))
  }
  const memo = new Map(), visiting = new Set()
  const floatOf = (id) => {
    if (memo.has(id)) return memo.get(id)
    if (visiting.has(id)) return Infinity   // a loop in the links — never let it hang the chart
    visiting.add(id)
    const r = byId.get(id), fin = parseDate(r.latest_finish)
    const succs = succOf.get(id) || []
    let f
    if (!succs.length) f = between(fin, addDays(end, 1))
    else f = Math.min(...succs.map(sid => between(fin, parseDate(byId.get(sid).earliest_start)) + floatOf(sid)))
    visiting.delete(id)
    memo.set(id, f)
    return f
  }
  const ids = new Set()
  for (const r of open) if (floatOf(r.id) <= 0) ids.add(r.id)
  return ids
}

/** Ids with zero or negative float, excluding locked rows — the critical path. */
export function computeCriticalIds(rows) {
  const ids = new Set()
  rows.forEach(r => { if (!isLocked(r)) { const s = rawSlackOf(r); if (s !== null && s <= 0) ids.add(r.id) } })
  return ids
}

/**
 * The visible date window: every dated row's span, padded, always including `anchorDate` (typically
 * "today" — see the module docstring on why both callers can just always pass todayUTC()).
 */
export function buildWindow(rows, px, anchorDate, { padBefore = 14, padAfter = 21 } = {}) {
  let windowStart = null, windowEnd = null
  rows.forEach(r => {
    const e = parseDate(r.earliest_start) || parseDate(r.latest_finish)
    const f = parseDate(r.latest_finish) || parseDate(r.earliest_start)
    if (e && (!windowStart || e < windowStart)) windowStart = e
    if (f && (!windowEnd || f > windowEnd)) windowEnd = f
  })
  if (!windowStart || anchorDate < windowStart) windowStart = anchorDate
  if (!windowEnd || anchorDate > windowEnd) windowEnd = anchorDate
  windowStart = addDays(windowStart, -padBefore)
  windowEnd = addDays(windowEnd, padAfter)
  const totalDays = Math.max(dayDiff(windowStart, windowEnd), 1)
  const timelineW = totalDays * px
  const xOf = (dateStr) => { const d = parseDate(dateStr); return d ? dayDiff(windowStart, d) * px : 0 }
  const anchorX = (anchorDate >= windowStart && anchorDate <= windowEnd) ? dayDiff(windowStart, anchorDate) * px : null
  return { windowStart, windowEnd, totalDays, timelineW, xOf, anchorX }
}

function isoWeek(dt) {
  const t = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()))
  t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7) + 3)
  const firstThu = new Date(Date.UTC(t.getUTCFullYear(), 0, 4))
  firstThu.setUTCDate(firstThu.getUTCDate() - ((firstThu.getUTCDay() + 6) % 7) + 3)
  return 1 + Math.round((t - firstThu) / (7 * 86400000))
}

/** Per-day cells (days-scale header/gridlines/weekend shading) + month/week header segments. */
export function buildDayCells(windowStart, totalDays, px) {
  const dayCells = []
  let d = new Date(windowStart.getTime())
  for (let i = 0; i <= totalDays; i++) {
    const dow = d.getUTCDay()
    dayCells.push({ i, x: i * px, date: new Date(d.getTime()), dayNum: d.getUTCDate(), dow, weekend: dow === 0 || dow === 6, monthStart: d.getUTCDate() === 1, weekStart: dow === 1 })
    d = addDays(d, 1)
  }
  const monthSegs = [], weekSegs = []
  dayCells.forEach(c => {
    if (c.i === 0 || c.monthStart) monthSegs.push({ x: c.x, label: c.date.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }) })
    if (c.i === 0 || c.weekStart) weekSegs.push({ x: c.x, wk: 'W' + isoWeek(c.date), date: c.date })
  })
  const weekCells = weekSegs.map((w, i) => ({ x: w.x, date: w.date, label: w.date.getUTCDate(), w: (i + 1 < weekSegs.length ? weekSegs[i + 1].x - w.x : 7 * px) }))
  return { dayCells, monthSegs, weekSegs, weekCells }
}

/**
 * Group rows by phase and assign y positions — phase header rows + positioned milestone rows.
 *
 * @param {Array} rows
 * @param {(row) => string} phaseKeyFor    stable grouping key for a row
 * @param {(key) => string} phaseLabelFor  display name for a phase key
 * @param {string[]} orderedKeys           known phase keys in schedule order (leftover keys append after)
 * @param {Set<string>} collapsedKeys      phase keys currently collapsed
 * @param {number} rowH
 * @param {(dateStr) => number} xOf
 * @param {number} px  pixels per day (zoom) — a same-day span still gets one full day-cell width
 * @returns {{ layout: Array, msPos: Map, bodyH: number, lineNumMap: Map }}
 *   `layout` entries are `{kind:'phase', pid, name, top, collapsed, count, gs, ge}` or
 *   `{kind:'ms', row, top, lineNum}`. `msPos` maps row id -> `{top,left,right,mid,barTop,barBottom}`.
 */
export function buildPhaseLayout({ rows, phaseKeyFor, phaseLabelFor, orderedKeys, collapsedKeys, rowH, xOf, px }) {
  const groupsMap = new Map()
  rows.forEach(r => { const pid = phaseKeyFor(r); if (!groupsMap.has(pid)) groupsMap.set(pid, []); groupsMap.get(pid).push(r) })
  const orderedPids = []
  ;(orderedKeys || []).forEach(k => { if (groupsMap.has(k)) orderedPids.push(k) })
  ;[...groupsMap.keys()].forEach(pid => { if (!orderedPids.includes(pid)) orderedPids.push(pid) })

  // Stable line numbers across all milestones regardless of collapse state, so DPD references stay
  // consistent as phases expand/collapse.
  const lineNumMap = new Map()
  let lineCounter = 0
  orderedPids.forEach(pid => { (groupsMap.get(pid) || []).forEach(r => { lineCounter++; lineNumMap.set(r.id, lineCounter) }) })

  const layout = []
  const msPos = new Map()
  let y = 0
  orderedPids.forEach(pid => {
    const groupRows = groupsMap.get(pid) || []
    const collapsed = collapsedKeys.has(pid)
    let gs = null, ge = null
    groupRows.forEach(r => { const e = parseDate(r.earliest_start), f = parseDate(r.latest_finish); if (e && (!gs || e < gs)) gs = e; if (f && (!ge || f > ge)) ge = f })
    layout.push({ kind: 'phase', pid, name: phaseLabelFor(pid), top: y, collapsed, count: groupRows.length, gs: gs ? formatDate(gs) : null, ge: ge ? formatDate(ge) : null })
    y += rowH
    if (!collapsed) {
      groupRows.forEach(r => {
        const left = xOf(r.earliest_start || r.latest_finish)
        const right = Math.max(xOf(r.latest_finish || r.earliest_start), left + px)
        msPos.set(r.id, { top: y, left, right, mid: y + rowH / 2, barTop: y + 7, barBottom: y + rowH - 7 })
        layout.push({ kind: 'ms', row: r, top: y, lineNum: lineNumMap.get(r.id) })
        y += rowH
      })
    }
  })
  return { layout, msPos, bodyH: y, lineNumMap }
}

/** Midpoint along an elbow path (vertical then horizontal) — where a remove-dependency "×" sits. */
export function elbowMidpoint(x1, y1, x2, y2) {
  const vLen = Math.abs(y2 - y1), hLen = Math.abs(x2 - x1)
  const half = (vLen + hLen) / 2
  if (half <= vLen) return { x: x1, y: y1 + Math.sign(y2 - y1) * half }
  return { x: x1 + Math.sign(x2 - x1) * (half - vLen), y: y2 }
}

/** One dependency edge's connector geometry, given the already-positioned bars (`msPos`). */
export function computeDepGeometry(dep, msPos, px, criticalIds) {
  const p = msPos.get(dep.predecessor_id), c = msPos.get(dep.milestone_id)
  if (!p || !c) return null
  // Drop from the middle of the predecessor's last day cell, not the cell boundary — reads less
  // cramped against the successor's start.
  const x1 = p.right - px / 2, x2 = c.left, y1 = p.mid, y2 = c.mid, xEnd = x2 - 2
  const path = `M ${x1} ${y1} V ${y2} H ${xEnd}`
  const crit = criticalIds.has(dep.predecessor_id) && criticalIds.has(dep.milestone_id)
  return { x1, y1, x2, y2, xEnd, path, crit, depKey: dep.milestone_id + '|' + dep.predecessor_id }
}
